package er.prateek.crossly.ui.screens.crossword

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.progression.DailyChallenge
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.LoadingState
import er.prateek.crossly.ui.gameplay.GameplayState

/**
 * Resolves today's random challenge (word, math or riddles), then plays it through the same
 * [CrosswordScreen] used for level play, flagged as daily so completion
 * updates the streak instead of level stars.
 */
@Composable
fun DailyPuzzleScreen(
    gameplayState: GameplayState,
    onBack: () -> Unit,
    onPlayMath: (levelId: String) -> Unit,
    onPlayRiddles: (levelId: String) -> Unit,
    onSolved: (puzzleId: String, stars: Int, elapsedMs: Long) -> Unit,
) {
    var puzzleId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        val today = AppContainer.dailyPuzzleManager.todayIso()
        // Resuming today's in-progress word daily keeps its puzzle; otherwise roll a new random one.
        val resumable = AppContainer.savedSessionManager.session
            ?.takeIf { it.isDaily && it.dailyDateIso == today }
        if (resumable != null) {
            puzzleId = resumable.puzzleId
            return@LaunchedEffect
        }
        when (
            val challenge = AppContainer.dailyPuzzleManager.randomChallenge(
                math = AppContainer.mathProgressionManager,
                riddles = AppContainer.riddleProgressionManager,
                modes = AppContainer.appConfig().modes,
            )
        ) {
            is DailyChallenge.Crossword -> puzzleId = challenge.puzzle.id
            is DailyChallenge.MathCrossword -> onPlayMath(challenge.levelId)
            is DailyChallenge.Riddles -> onPlayRiddles(challenge.levelId)
        }
    }

    val currentPuzzleId = puzzleId
    if (currentPuzzleId == null) {
        GameScreen { LoadingState(modifier = Modifier.fillMaxSize()) }
        return
    }

    CrosswordScreen(
        puzzleId = currentPuzzleId,
        gameplayState = gameplayState,
        isDaily = true,
        onBack = onBack,
        onSolved = onSolved,
    )
}
